// Fetch a case-bucket object through a signed URL, check it really is the file
// type we expect, and save it under its real filename.
//
// Why the check: fetch() resolves for any HTTP status, and an expired signed URL
// answers with an S3 <Error> XML body. Without a check, that error document gets
// saved as "memo.docx" (see the same guard for PDFs in pdfDocumentSource.js).
// The filename is never put in a URL: the browser names the download from the
// record, through an anchor on an in-memory blob.

export const DOCUMENT_FORMATS = {
  pdf: { label: 'PDF', extension: '.pdf', mime: 'application/pdf' },
  docx: {
    label: 'Word',
    extension: '.docx',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  xlsx: {
    label: 'Excel',
    extension: '.xlsx',
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
};

const PDF_HEADER_SCAN_BYTES = 1024;
const PDF_HEADER = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
const ZIP_HEADER = [0x50, 0x4b, 0x03, 0x04]; // PK.. (Word and Excel files are zip packages)

const startsWith = (bytes, signature, offset = 0) =>
  signature.every((b, i) => bytes[offset + i] === b);

/** True when the bytes look like a file of `format` ('pdf' | 'docx' | 'xlsx'). */
export const looksLikeFormat = (buffer, format) => {
  if (!buffer || !buffer.byteLength) return false;
  if (format === 'pdf') {
    const head = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, PDF_HEADER_SCAN_BYTES));
    for (let i = 0; i + PDF_HEADER.length <= head.length; i += 1) {
      if (startsWith(head, PDF_HEADER, i)) return true;
    }
    return false;
  }
  if (format === 'docx' || format === 'xlsx') {
    return startsWith(new Uint8Array(buffer, 0, Math.min(buffer.byteLength, ZIP_HEADER.length)), ZIP_HEADER);
  }
  return false;
};

/**
 * Fetch the bytes behind a signed URL.
 * @throws if the response is not OK or the body is not a `format` file.
 */
export const fetchDocumentBytes = async (url, format) => {
  if (!url) throw new Error('The file link is not ready yet.');
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`File fetch failed: ${response.status}`);
  }
  const buffer = await response.arrayBuffer();
  if (!looksLikeFormat(buffer, format)) {
    throw new Error('The file link returned something other than the file (it may have expired).');
  }
  return buffer;
};

/** Add the format's extension when the filename lacks it. */
export const withExtension = (filename, format) => {
  const ext = DOCUMENT_FORMATS[format]?.extension || '';
  const name = (filename || '').trim() || 'document';
  return ext && !name.toLowerCase().endsWith(ext) ? `${name}${ext}` : name;
};

/** Save bytes to the user's disk as `filename`. */
export const saveBytesAsFile = (buffer, filename, mime = 'application/octet-stream') => {
  const blob = new Blob([buffer], { type: mime });
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};
